import { useState, useMemo, useCallback } from 'react';
import { mobileAPI } from '../../api/MobileAPI';
import { findImprovements } from '../../services/utils/improvements';
import { getCountyCodeForClub, getRegionCodeForClub } from '../../services/utils/clubs';
const MAX_GUEST_SWIMMERS = 1;
export function useSwimmerData({ showToast, isAuthenticated }) {
    const [loading, setLoading] = useState(false);
    const [savedSwimmers, setSavedSwimmers] = useState([]);
    const [currentSwimmerData, setCurrentSwimmerData] = useState(null);
    const [comparisonResult, setComparisonResult] = useState(null);
    const [comparisonSwimmers, setComparisonSwimmers] = useState([]);
    const [improvementsModal, setImprovementsModal] = useState(null);
    const savedTirefs = useMemo(() => new Set(savedSwimmers.map(s => s.tiref)), [savedSwimmers]);
    const isCurrentSwimmerSaved = !!currentSwimmerData && savedTirefs.has(currentSwimmerData.tiref);
    const loadSavedSwimmers = useCallback(async () => {
        try {
            const swimmers = await mobileAPI.getSavedSwimmers();
            setSavedSwimmers(swimmers);
        }
        catch (error) {
            showToast(`Error loading saved swimmers: ${error.message}`, 'error');
        }
    }, [showToast]);
    const handleSearchResults = useCallback(async (tiref, name, birthYear, gender, club) => {
        setLoading(true);
        try {
            const times = await mobileAPI.getSwimmerTimes(tiref);
            const selectedCountyCode = getCountyCodeForClub(club);
            const selectedRegionCode = getRegionCodeForClub(club);
            setCurrentSwimmerData({ tiref, name, birthYear, gender, club, region: '', times, selectedCountyCode, selectedRegionCode });
            showToast('Times loaded successfully');
        }
        catch (error) {
            showToast(`Error loading times: ${error.message}`, 'error');
        }
        finally {
            setLoading(false);
        }
    }, [showToast]);
    const handleSaveSwimmer = useCallback(async () => {
        if (!currentSwimmerData) {
            showToast('No swimmer data to save', 'error');
            return;
        }
        if (!isAuthenticated && !savedTirefs.has(currentSwimmerData.tiref) && savedSwimmers.length >= MAX_GUEST_SWIMMERS) {
            showToast('Sign in to save more than 1 swimmer', 'error');
            return;
        }
        setLoading(true);
        try {
            const dataToSave = {
                ...currentSwimmerData,
                selectedCountyCode: currentSwimmerData.selectedCountyCode ?? getCountyCodeForClub(currentSwimmerData.club),
                selectedRegionCode: currentSwimmerData.selectedRegionCode ?? getRegionCodeForClub(currentSwimmerData.club),
            };
            await mobileAPI.saveSwimmer(dataToSave);
            await loadSavedSwimmers();
            showToast('Swimmer saved successfully');
        }
        catch (error) {
            showToast(`Error saving swimmer: ${error.message}`, 'error');
        }
        finally {
            setLoading(false);
        }
    }, [currentSwimmerData, showToast, loadSavedSwimmers, isAuthenticated, savedTirefs, savedSwimmers.length]);
    const handleSaveFromSearch = useCallback(async (tiref, name, birthYear, gender, club) => {
        if (!isAuthenticated && !savedTirefs.has(tiref) && savedSwimmers.length >= MAX_GUEST_SWIMMERS) {
            showToast('Sign in to save more than 1 swimmer', 'error');
            return;
        }
        setLoading(true);
        try {
            const times = await mobileAPI.getSwimmerTimes(tiref);
            const selectedCountyCode = getCountyCodeForClub(club);
            const selectedRegionCode = getRegionCodeForClub(club);
            await mobileAPI.saveSwimmer({ tiref, name, birthYear, gender, club, region: '', times, selectedCountyCode, selectedRegionCode });
            await loadSavedSwimmers();
            showToast(`Saved ${name}`);
        }
        catch (error) {
            showToast(`Error saving swimmer: ${error.message}`, 'error');
        }
        finally {
            setLoading(false);
        }
    }, [showToast, loadSavedSwimmers, isAuthenticated, savedTirefs, savedSwimmers.length]);
    const handleExportToExcel = useCallback(async () => {
        if (!currentSwimmerData) {
            showToast('No swimmer data to export', 'error');
            return;
        }
        setLoading(true);
        try {
            const filePath = await mobileAPI.exportToExcel(currentSwimmerData, comparisonResult);
            showToast(`Exported to: ${filePath}`);
        }
        catch (error) {
            showToast(`Error exporting: ${error.message}`, 'error');
        }
        finally {
            setLoading(false);
        }
    }, [currentSwimmerData, comparisonResult, showToast]);
    const handleLoadSavedSwimmer = useCallback((swimmer) => {
        setCurrentSwimmerData(swimmer);
        setComparisonResult(null);
    }, []);
    const handleDeleteSwimmer = useCallback(async (tiref) => {
        setLoading(true);
        try {
            await mobileAPI.deleteSwimmer(tiref);
            await loadSavedSwimmers();
            showToast('Swimmer deleted successfully');
            if (currentSwimmerData?.tiref === tiref)
                setCurrentSwimmerData(null);
        }
        catch (error) {
            showToast(`Error deleting swimmer: ${error.message}`, 'error');
        }
        finally {
            setLoading(false);
        }
    }, [currentSwimmerData?.tiref, showToast, loadSavedSwimmers]);
    const handleClearDetails = useCallback(() => {
        setCurrentSwimmerData(null);
        setComparisonResult(null);
    }, []);
    const handleUpdateSwimmerTags = useCallback(async (tiref, tags) => {
        setLoading(true);
        try {
            const swimmer = savedSwimmers.find(s => s.tiref === tiref);
            if (!swimmer)
                throw new Error('Swimmer not found');
            const updatedSwimmer = { ...swimmer, tags: tags.length > 0 ? tags : undefined };
            await mobileAPI.saveSwimmer(updatedSwimmer);
            await loadSavedSwimmers();
            showToast(tags.length > 0 ? 'Tags updated' : 'Tags Cleared');
        }
        catch (error) {
            showToast(`Error updating tags: ${error.message}`, 'error');
        }
        finally {
            setLoading(false);
        }
    }, [savedSwimmers, showToast, loadSavedSwimmers]);
    const handleUpdateSwimmerNotifications = useCallback(async (tiref, enabled) => {
        setLoading(true);
        try {
            const swimmer = savedSwimmers.find(s => s.tiref === tiref);
            if (!swimmer)
                throw new Error('Swimmer not found');
            const updatedSwimmer = { ...swimmer, notificationsEnabled: enabled };
            const saved = await mobileAPI.saveSwimmer(updatedSwimmer);
            setSavedSwimmers(prev => prev.map(s => s.tiref === tiref ? saved : s));
            showToast(enabled ? 'Notifications enabled' : 'Notifications disabled');
        }
        catch (error) {
            showToast(`Error updating notifications: ${error.message}`, 'error');
        }
        finally {
            setLoading(false);
        }
    }, [savedSwimmers, showToast]);
    const handleRefreshCurrentSwimmer = useCallback(async () => {
        if (!currentSwimmerData)
            return;
        setLoading(true);
        try {
            const oldTimes = currentSwimmerData.times;
            const times = await mobileAPI.getSwimmerTimes(currentSwimmerData.tiref);
            const swimmerData = { ...currentSwimmerData, times };
            await mobileAPI.saveSwimmer(swimmerData);
            await loadSavedSwimmers();
            setCurrentSwimmerData(swimmerData);
            const improvements = findImprovements(oldTimes, times);
            if (improvements.length > 0) {
                setImprovementsModal([{ swimmerName: currentSwimmerData.name, improvements }]);
            }
            else {
                showToast('Times refreshed successfully');
            }
        }
        catch (error) {
            showToast(`Error refreshing times: ${error.message}`, 'error');
        }
        finally {
            setLoading(false);
        }
    }, [currentSwimmerData, showToast, loadSavedSwimmers]);
    const handleRefreshAllSwimmers = useCallback(async (swimmersToRefresh = savedSwimmers) => {
        if (savedSwimmers.length === 0) {
            showToast('No swimmers to refresh', 'error');
            return;
        }
        const scope = swimmersToRefresh.length === savedSwimmers.length ? 'all' : `the ${swimmersToRefresh.length} shown`;
        if (!confirm(`This will reload ${scope} saved swimmers from the website to update their club information and times. This may take a few moments. Continue?`))
            return;
        setLoading(true);
        let successCount = 0;
        let errorCount = 0;
        const allPBs = [];
        try {
            for (const swimmer of swimmersToRefresh) {
                try {
                    const times = await mobileAPI.getSwimmerTimes(swimmer.tiref);
                    await mobileAPI.saveSwimmer({ ...swimmer, times });
                    const improvements = findImprovements(swimmer.times, times);
                    if (improvements.length > 0)
                        allPBs.push({ swimmerName: swimmer.name, improvements });
                    successCount++;
                }
                catch (error) {
                    console.error(`Error refreshing swimmer ${swimmer.name}:`, error);
                    errorCount++;
                }
            }
            await loadSavedSwimmers();
            if (allPBs.length > 0) {
                setImprovementsModal(allPBs);
            }
            else if (errorCount === 0) {
                showToast(`Successfully refreshed ${successCount} swimmers${successCount === 1 ? '' : 's'}!`);
            }
            else {
                showToast(`Refreshed ${successCount} swimmers, ${errorCount} failed`, 'error');
            }
        }
        catch (error) {
            showToast(`Error during refresh: ${error.message}`, 'error');
        }
        finally {
            setLoading(false);
        }
    }, [savedSwimmers, showToast, loadSavedSwimmers]);
    const handleCompareSwimmers = useCallback((swimmers) => {
        setComparisonSwimmers(swimmers);
        setCurrentSwimmerData(null);
    }, []);
    const handleCloseComparison = useCallback(() => {
        setComparisonSwimmers([]);
    }, []);
    const handleCountySelected = useCallback(async (county) => {
        if (!currentSwimmerData)
            return;
        const updated = { ...currentSwimmerData, preferredCounty: county };
        setCurrentSwimmerData(updated);
        await mobileAPI.saveSwimmer(updated);
    }, [currentSwimmerData]);
    const handleUpdateSwimmerRegion = useCallback(async (regionCode) => {
        if (!currentSwimmerData)
            return;
        const updated = { ...currentSwimmerData, selectedRegionCode: regionCode };
        setCurrentSwimmerData(updated);
        await mobileAPI.saveSwimmer(updated);
    }, [currentSwimmerData]);
    const handleUpdateSwimmerCounty = useCallback(async (countyCode) => {
        if (!currentSwimmerData)
            return;
        const updated = { ...currentSwimmerData, selectedCountyCode: countyCode };
        setCurrentSwimmerData(updated);
        await mobileAPI.saveSwimmer(updated);
    }, [currentSwimmerData]);
    const handleUpdateCombinedAgeGroup = useCallback(async (ages) => {
        if (!currentSwimmerData)
            return;
        const updated = {
            ...currentSwimmerData,
            combinedAgeGroup: ages,
            // Cached combined rankings were ranked against the old group, so they no longer
            // dscribe the new one. Dropping them shows Fetch pills rather than stale number.
            combinedRankings: undefined,
        };
        setCurrentSwimmerData(updated);
        await mobileAPI.saveSwimmer(updated);
    }, [currentSwimmerData]);
    const clearSwimmerRecentPBs = useCallback(async (swimmer) => {
        await mobileAPI.saveSwimmer({ ...swimmer, recentPBs: undefined });
    }, []);
    return {
        loading,
        setLoading,
        savedSwimmers,
        currentSwimmerData,
        setCurrentSwimmerData,
        comparisonResult,
        setComparisonResult,
        comparisonSwimmers,
        improvementsModal,
        setImprovementsModal,
        savedTirefs,
        isCurrentSwimmerSaved,
        loadSavedSwimmers,
        handleSearchResults,
        handleSaveSwimmer,
        handleSaveFromSearch,
        handleExportToExcel,
        handleLoadSavedSwimmer,
        handleDeleteSwimmer,
        handleClearDetails,
        handleUpdateSwimmerTags,
        handleUpdateSwimmerNotifications,
        handleRefreshCurrentSwimmer,
        handleRefreshAllSwimmers,
        handleCompareSwimmers,
        handleCloseComparison,
        handleCountySelected,
        handleUpdateSwimmerRegion,
        handleUpdateSwimmerCounty,
        handleUpdateCombinedAgeGroup,
        clearSwimmerRecentPBs,
    };
}
